<vs-card class="overflow-hidden">
  <vs-card-header
    [config]="{
      title: 'Rating Matrix',
      subtitle: activeView === 'matrix'
        ? 'Define values at dimensional intersections for pricing, class guides, or other rules—for any type of covered item. Select dimensions for rows, columns, and filters, then edit cell values.'
        : 'View the full rating matrix configuration for this plan, grouped by dimensional depth and unique dimension combinations.'
    }"
    class="[&_h2]:!text-base [&_h2]:!text-gray-900"
  >
    <button
      *ngIf="activeView === 'matrix'"
      vs-button
      slot="end"
      size="sm"
      class="!text-sm"
      [disabled]="saving"
      (click)="saveChanges()"
    >
      {{ saving ? 'Saving...' : 'Save changes' }}
    </button>

    <button
      *ngIf="activeView === 'report' && canEditMatrix && !isEditingReport"
      vs-button
      slot="end"
      appearance="outline"
      size="sm"
      class="!text-sm"
      (click)="startReportEdit()"
    >
      <vs-icon slot="start" name="edit-01"></vs-icon>
      Edit
    </button>

    <div *ngIf="activeView === 'report' && isReportEditable" slot="end" class="flex items-center gap-2">
      <button
        vs-button
        appearance="outline"
        size="sm"
        class="!text-sm"
        [disabled]="isSavingReport"
        (click)="exitReportEdit()"
      >
        Cancel
      </button>
      <button
        vs-button
        size="sm"
        class="!text-sm"
        [disabled]="!hasReportChanges || isSavingReport"
        (click)="saveReportChanges()"
      >
        {{ isSavingReport ? 'Saving...' : 'Save changes' }}
      </button>
    </div>
  </vs-card-header>

  <div class="px-2 pt-2">
    <vs-tabs theme="primary" class="mb-2" (tabChanged)="onViewChange($event)">
      <vs-tab [config]="{ label: 'Matrix' }"></vs-tab>
      <vs-tab [config]="{ label: 'Configuration Report' }"></vs-tab>
    </vs-tabs>
  </div>

  <div *ngIf="activeView === 'report'" class="p-2">
    <div *ngIf="loadingReport" class="p-4 text-sm text-gray-500">Loading configuration report...</div>

    <div
      *ngIf="!loadingReport && configurationReport && configurationReport.sections.length === 0"
      class="p-4 text-sm text-gray-500 italic"
    >
      No rating matrix configuration found for this plan.
    </div>

    <div *ngIf="!loadingReport && configurationReport && configurationReport.sections.length > 0" class="space-y-6">
      <div *ngFor="let section of configurationReport.sections" class="space-y-4">
        <h3 class="text-sm font-semibold text-gray-900">{{ getReportDepthLabel(section.depth) }}</h3>

        <div *ngFor="let table of section.tables" class="overflow-hidden border rounded">
          <div class="flex items-center justify-between gap-2 border-b bg-slate-50 px-3 py-1.5">
            <span class="text-xs font-semibold text-gray-700">{{ getReportTableLabel(table) }}</span>
            <button
              vs-button
              appearance="outline"
              size="sm"
              class="!text-xs !px-2 !py-1 !h-auto"
              [title]="'Open ' + getReportTableLabel(table) + ' in the matrix'"
              [disabled]="!!openingLayoutTableKey"
              (click)="openReportTableLayout(table, layoutDialog)"
            >
              <vs-icon slot="start" name="grid-01" size="14"></vs-icon>
              {{ isOpeningLayout(table) ? 'Opening...' : 'Open in matrix' }}
            </button>
          </div>

          <div class="overflow-auto">
            <table class="w-full min-w-full text-xs border-collapse table-fixed">
              <thead class="bg-slate-50 border-b">
                <tr>
                  <th
                    *ngFor="let header of table.headers"
                    class="px-3 py-2 text-left font-semibold border-r last:border-r-0"
                    [style.width.%]="getReportDimensionColumnWidth(table)"
                  >
                    {{ header }}
                  </th>
                  <th class="px-3 py-2 text-left font-semibold" [style.width.%]="reportValueColumnWidth">Value</th>
                  <th
                    *ngIf="isReportEditable"
                    class="px-3 py-2 text-right font-semibold"
                    [style.width.%]="reportActionsColumnWidth"
                  >
                    <span class="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  *ngFor="let row of table.rows; let rowIndex = index"
                  class="border-t"
                  [class.bg-gray-50]="rowIndex % 2 === 1"
                >
                  <td
                    *ngFor="let element of row.elements"
                    class="px-3 py-2 border-r last:border-r-0 truncate"
                    [style.width.%]="getReportDimensionColumnWidth(table)"
                    [title]="getReportElementLabel(element)"
                  >
                    {{ getReportElementLabel(element) }}
                  </td>

                  <td class="px-3 py-2 font-medium" [style.width.%]="reportValueColumnWidth">
                    <ng-container *ngIf="isReportEditable; else readonlyReportValue">
                      <div class="flex items-center gap-1">
                        <input
                          *ngIf="row.valueType !== 'FORMULA'; else reportFormulaLabel"
                          class="vs-input flex-1 min-w-0 text-xs py-1 px-2 border"
                          [class.border-gray-300]="!isReportRowDirty(row)"
                          [class.border-primary-400]="isReportRowDirty(row)"
                          [class.bg-primary-50]="isReportRowDirty(row)"
                          type="number"
                          step="0.01"
                          [ngModel]="getReportRowValue(row)"
                          (ngModelChange)="onReportValueChange(row, $event)"
                          [disabled]="isSavingReport"
                          [attr.aria-label]="'Value for ' + getReportRowIntersectionLabel(row)"
                        />
                        <ng-template #reportFormulaLabel>
                          <button
                            type="button"
                            class="vs-input flex-1 min-w-0 text-left text-xs py-1 px-2 border border-gray-300 bg-gray-50"
                            [vsTooltip]="{ template: formulaTooltip, context: { formula: row.formula } }"
                            tooltipStyle="light"
                            [disabled]="isSavingReport"
                            (click)="openFormulaDialog(row, formulaDialog)"
                          >
                            {{ getFormulaLabel(row.formula) }}
                          </button>
                        </ng-template>
                        <button
                          *ngIf="canUseFormulaForRow(row)"
                          vs-icon-button
                          size="sm"
                          appearance="clear"
                          [disabled]="isSavingReport"
                          [attr.aria-label]="'Set value type for ' + getReportRowIntersectionLabel(row)"
                          title="Fixed amount or formula"
                          (click)="openFormulaDialog(row, formulaDialog)"
                        >
                          <vs-icon name="edit-01"></vs-icon>
                        </button>
                      </div>
                      <div *ngIf="getReportEffectiveWindowLabel(row) as window" class="mt-1 text-[10px] text-gray-500">
                        {{ window }}
                      </div>
                    </ng-container>
                    <ng-template #readonlyReportValue>
                      <span
                        *ngIf="row.valueType === 'FORMULA'; else readonlyFixedValue"
                        class="underline decoration-dotted"
                        [vsTooltip]="{ template: formulaTooltip, context: { formula: row.formula } }"
                        tooltipStyle="light"
                      >
                        {{ getReportValueWithEffectiveDates(row) }}
                      </span>
                      <ng-template #readonlyFixedValue>{{ getReportValueWithEffectiveDates(row) }}</ng-template>
                    </ng-template>
                  </td>

                  <td *ngIf="isReportEditable" class="px-3 py-2 text-right" [style.width.%]="reportActionsColumnWidth">
                    <button
                      vs-icon-button
                      size="sm"
                      theme="danger"
                      appearance="clear"
                      [disabled]="isSavingReport"
                      [attr.aria-label]="'Remove ' + getReportRowIntersectionLabel(row)"
                      (click)="openDeleteReportRowModal(row, deleteReportRowDialog)"
                    >
                      <vs-icon name="trash-01"></vs-icon>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <vs-dialog #deleteReportRowDialog [config]="{ dialogContainerClass: 'max-w-[500px]' }">
      <vs-dialog-header
        [config]="{
          mode: 'simple',
          icon: 'alert-triangle',
          iconTheme: 'danger',
          title: 'Remove Value',
          subtitle: 'This removes the value from the rating matrix and cannot be undone.'
        }"
        (close)="closeDeleteReportRowModal(deleteReportRowDialog)"
      ></vs-dialog-header>

      <div class="mb-4 rounded border bg-slate-50 px-3 py-2 text-xs">
        <div class="text-gray-500">Intersection</div>
        <div class="font-medium text-gray-900">{{ deletingReportRowLabel }}</div>
        <div class="mt-2 text-gray-500">Value</div>
        <div class="font-medium text-gray-900">{{ deletingReportRowValue }}</div>
      </div>

      <div class="flex gap-2">
        <button
          vs-button
          appearance="outline"
          class="flex-1"
          [disabled]="isRemovingReportRow"
          (click)="closeDeleteReportRowModal(deleteReportRowDialog)"
        >
          Cancel
        </button>
        <button
          vs-button
          theme="danger"
          class="flex-1"
          [disabled]="isRemovingReportRow"
          (click)="confirmDeleteReportRow(deleteReportRowDialog)"
        >
          <vs-icon
            slot="start"
            [class.animate-spin]="isRemovingReportRow"
            [name]="isRemovingReportRow ? 'refresh-cw-04' : 'trash-01'"
          ></vs-icon>
          {{ isRemovingReportRow ? 'Removing...' : 'Remove' }}
        </button>
      </div>
    </vs-dialog>

    <vs-dialog #layoutDialog [config]="{ dialogContainerClass: 'min-w-[520px] max-w-[640px]' }">
      <vs-dialog-header
        [config]="{
          mode: 'simple',
          icon: 'grid-01',
          iconTheme: 'primary',
          title: 'Open in matrix',
          subtitle: 'Choose where each dimension sits before opening the grid.'
        }"
        (close)="closeReportTableLayout(layoutDialog)"
      ></vs-dialog-header>

      <div class="mb-3 text-xs text-gray-500">{{ layoutDialogTitle }}</div>

      <div class="space-y-2">
        <div
          *ngFor="let entry of matrixLayoutDraft"
          class="flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors"
          [class.border-themed-300]="entry.type !== 'filter'"
          [class.bg-themed-50]="entry.type !== 'filter'"
        >
          <vs-icon
            [name]="getLayoutIcon(entry.type)"
            size="18"
            [class.text-themed-600]="entry.type !== 'filter'"
            [class.text-gray-400]="entry.type === 'filter'"
          ></vs-icon>

          <div class="flex-1 min-w-0">
            <div class="truncate text-sm font-medium text-gray-900">{{ entry.title }}</div>
            <div class="text-xs text-gray-500">{{ entry.count }} element{{ entry.count === 1 ? '' : 's' }}</div>
          </div>

          <div class="flex gap-0.5 rounded-lg bg-gray-100 p-0.5">
            <button
              vs-button
              size="xs"
              [appearance]="entry.type === 'row' ? 'default' : 'clear'"
              (click)="setLayoutPlacement(entry, 'row')"
            >
              Row
            </button>
            <button
              vs-button
              size="xs"
              [appearance]="entry.type === 'col' ? 'default' : 'clear'"
              (click)="setLayoutPlacement(entry, 'col')"
            >
              Column
            </button>
            <button
              vs-button
              size="xs"
              [appearance]="entry.type === 'filter' ? 'default' : 'clear'"
              (click)="setLayoutPlacement(entry, 'filter')"
            >
              Filter
            </button>
          </div>
        </div>
      </div>

      <div class="mt-4 rounded-lg border bg-gray-50 px-3 py-2.5 space-y-1.5">
        <div class="flex items-baseline gap-2 text-xs">
          <vs-icon name="rows-01" size="14" class="text-themed-600 shrink-0 translate-y-0.5"></vs-icon>
          <span class="w-14 shrink-0 text-gray-500">Rows</span>
          <span class="flex-1 truncate font-medium text-gray-900">{{ getLayoutNames('row') || '—' }}</span>
          <span class="shrink-0 text-gray-500">{{ layoutRowTotal }}</span>
        </div>

        <div class="flex items-baseline gap-2 text-xs">
          <vs-icon name="columns-01" size="14" class="text-themed-600 shrink-0 translate-y-0.5"></vs-icon>
          <span class="w-14 shrink-0 text-gray-500">Columns</span>
          <span class="flex-1 truncate font-medium text-gray-900">{{ getLayoutNames('col') || '—' }}</span>
          <span
            class="shrink-0"
            [class.text-danger-600]="isLayoutColumnHeavy"
            [class.font-medium]="isLayoutColumnHeavy"
            [class.text-gray-500]="!isLayoutColumnHeavy"
          >
            {{ layoutColTotal }}
          </span>
        </div>

        <div *ngIf="getLayoutNames('filter')" class="flex items-baseline gap-2 text-xs">
          <vs-icon name="filter-lines" size="14" class="text-gray-400 shrink-0 translate-y-0.5"></vs-icon>
          <span class="w-14 shrink-0 text-gray-500">Filters</span>
          <span class="flex-1 truncate font-medium text-gray-900">{{ getLayoutNames('filter') }}</span>
        </div>
      </div>

      <div *ngIf="isLayoutColumnHeavy" class="mt-2 flex items-start gap-2 text-xs text-danger-600">
        <vs-icon name="alert-triangle" size="14" class="shrink-0 translate-y-0.5"></vs-icon>
        <span>{{ layoutColTotal }} columns won't fit on screen — move a dimension to Row or Filter.</span>
      </div>

      <div *ngIf="!canApplyLayout" class="mt-2 flex items-start gap-2 text-xs text-gray-500">
        <vs-icon name="info-circle" size="14" class="shrink-0 translate-y-0.5"></vs-icon>
        <span>Pick at least one Row or Column — a grid of only filters has nothing to show.</span>
      </div>

      <div class="mt-4 flex gap-2">
        <button vs-button appearance="outline" class="flex-1" (click)="closeReportTableLayout(layoutDialog)">
          Cancel
        </button>
        <button vs-button class="flex-1" [disabled]="!canApplyLayout" (click)="applyReportTableLayout(layoutDialog)">
          <vs-icon slot="start" name="grid-01" size="16"></vs-icon>
          Open matrix
        </button>
      </div>
    </vs-dialog>
  </div>

  <ng-template #formulaTooltip let-formula="formula">
    <div class="flex flex-col gap-1">
      <p class="text-sm font-semibold">{{ formula?.percent }}% of</p>
      <p class="text-xs text-gray-500" *ngFor="let title of getFormulaChargeTitles(formula)">{{ title }}</p>
    </div>
  </ng-template>

  <vs-dialog #formulaDialog [config]="{ dialogContainerClass: 'min-w-[460px] max-w-[560px]' }">
    <vs-dialog-header
      [config]="{
        mode: 'simple',
        showDivider: true,
        icon: 'edit-01',
        iconTheme: 'primary',
        title: 'Edit Value',
        subtitle: formulaDialogLabel
      }"
      (close)="closeFormulaDialog(formulaDialog)"
    ></vs-dialog-header>

    <div class="p-4 space-y-4 text-sm">
      <div class="flex gap-2">
        <button
          vs-button
          size="sm"
          class="flex-1"
          [appearance]="formulaDialogMode === 'FIXED' ? 'default' : 'outline'"
          (click)="formulaDialogMode = 'FIXED'"
        >
          Fixed amount
        </button>
        <button
          vs-button
          size="sm"
          class="flex-1"
          [appearance]="formulaDialogMode === 'FORMULA' ? 'default' : 'outline'"
          (click)="formulaDialogMode = 'FORMULA'"
        >
          Percentage of charges
        </button>
      </div>

      <div *ngIf="formulaDialogMode === 'FIXED'">
        <div class="mb-1 text-xs text-gray-600">Amount</div>
        <input
          class="vs-input w-full text-sm py-1 px-2 border border-gray-300"
          type="number"
          step="0.01"
          [(ngModel)]="formulaDialogValue"
        />
      </div>

      <ng-container *ngIf="formulaDialogMode === 'FORMULA'">
        <div>
          <div class="mb-1 text-xs text-gray-600">Percentage</div>
          <input
            class="vs-input w-full text-sm py-1 px-2 border border-gray-300"
            type="number"
            step="0.01"
            [(ngModel)]="formulaDialogPercent"
          />
          <p class="mt-1 text-xs text-gray-500">Negative values act as a discount. Tax is added afterwards.</p>
        </div>

        <div>
          <div class="mb-1 text-xs text-gray-600">Of these charges</div>
          <p *ngIf="formulaFeeTypeOptions.length === 0" class="text-xs text-gray-500">No charge types available.</p>
          <label
            *ngFor="let feeType of formulaFeeTypeOptions"
            class="flex items-center gap-2 py-1 cursor-pointer select-none"
          >
            <input
              vsInput
              type="checkbox"
              [ngModel]="formulaDialogFeeTypeIds.has(feeType.id)"
              (ngModelChange)="toggleFormulaFeeType(feeType.id, $event)"
            />
            <span>{{ feeType.title }}</span>
          </label>
          <p class="mt-1 text-xs text-gray-500">
            Only matched base-coverage charges count. A seller's percentage also includes their parents' formula charges
            of the selected types.
          </p>
        </div>
      </ng-container>

      <div class="flex gap-2 justify-end">
        <button vs-button appearance="outline" [disabled]="isSavingFormula" (click)="closeFormulaDialog(formulaDialog)">
          Cancel
        </button>
        <button vs-button theme="primary" [disabled]="!canSaveFormulaDialog" (click)="saveFormulaDialog(formulaDialog)">
          {{ isSavingFormula ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>
  </vs-dialog>

  <div *ngIf="activeView === 'matrix'" class="p-2">
    <div class="flex flex-col gap-2">
      <!-- Top Row: Dimensions, Configuration, Asset -->
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[2fr_1.5fr_3fr] gap-2">
        <!-- Left Panel: Dimensions -->
        <vs-card
          class="overflow-hidden flex flex-col min-h-[320px] md:min-h-[360px] xl:min-h-[420px] max-h-[320px] md:max-h-[420px]"
        >
          <vs-card-header
            [config]="{ title: 'Dimensions' }"
            class="[&_h2]:!text-sm [&_h2]:!text-gray-900 !p-2"
          ></vs-card-header>
          <div class="p-2 overflow-y-auto flex-1 min-h-0">
            <div class="space-y-1.5">
              <div *ngFor="let dim of dimensions" class="border rounded p-2">
                <div class="font-medium text-sm mb-2">{{ dim.title }}</div>
                <div class="grid grid-cols-3 gap-1">
                  <button
                    vs-button
                    appearance="outline"
                    size="sm"
                    class="!w-full !text-xs !px-1.5 !py-1 !h-auto !whitespace-nowrap"
                    [class.bg-yellow-100]="isDimensionSelected(dim.id) === 'row'"
                    [disabled]="isDimensionSelected(dim.id) === 'col'"
                    (click)="addDimensionAsRow(dim.id)"
                  >
                    Add Row
                  </button>
                  <button
                    vs-button
                    appearance="outline"
                    size="sm"
                    class="!w-full !text-xs !px-1.5 !py-1 !h-auto !whitespace-nowrap"
                    [class.bg-green-100]="isDimensionSelected(dim.id) === 'col'"
                    [disabled]="isDimensionSelected(dim.id) === 'row'"
                    (click)="addDimensionAsColumn(dim.id)"
                  >
                    Add Column
                  </button>
                  <button
                    vs-button
                    appearance="outline"
                    size="sm"
                    class="!w-full !text-xs !px-1.5 !py-1 !h-auto !whitespace-nowrap"
                    [class.bg-blue-100]="isDimensionSelected(dim.id) === 'filter'"
                    (click)="addDimensionAsFilter(dim.id)"
                  >
                    Add Filter
                  </button>
                </div>
              </div>
              <div *ngIf="dimensions.length === 0 && !loading" class="text-xs text-gray-500 italic p-2 text-center">
                No dimensions available. Create dimensions to get started.
              </div>
            </div>
          </div>
        </vs-card>

        <!-- Center Panel: Configuration -->
        <vs-card
          class="overflow-hidden flex flex-col min-h-[320px] md:min-h-[360px] xl:min-h-[420px] max-h-[320px] md:max-h-[420px]"
        >
          <vs-card-header
            [config]="{ title: 'Configuration' }"
            class="[&_h2]:!text-sm [&_h2]:!text-gray-900 !p-2"
          ></vs-card-header>
          <div class="p-2 overflow-y-auto flex-1 min-h-0">
            <div class="space-y-2">
              <!-- Row List -->
              <div>
                <div class="text-xs font-medium mb-1">Row List</div>
                <div class="space-y-0.5">
                  <div
                    *ngFor="let rowDim of selectedRowDimensions; let i = index"
                    class="flex items-center gap-1 p-1.5 bg-gray-50 rounded text-xs"
                  >
                    <span class="flex-1">{{ rowDim.dimension.title }}</span>
                    <button
                      vs-button
                      appearance="clear"
                      size="sm"
                      class="!px-1 !min-w-[24px] !flex !items-center !justify-center"
                      [disabled]="i === 0"
                      (click)="moveRowDimensionUp(i)"
                    >
                      <vs-icon name="chevron-up" size="16"></vs-icon>
                    </button>
                    <button
                      vs-button
                      appearance="clear"
                      size="sm"
                      class="!px-1 !min-w-[24px] !flex !items-center !justify-center"
                      [disabled]="i === selectedRowDimensions.length - 1"
                      (click)="moveRowDimensionDown(i)"
                    >
                      <vs-icon name="chevron-down" size="16"></vs-icon>
                    </button>
                    <button
                      vs-button
                      appearance="clear"
                      size="sm"
                      theme="danger"
                      class="!px-1 !min-w-[24px] !flex !items-center !justify-center"
                      (click)="removeRowDimension(i)"
                    >
                      <vs-icon name="trash-03" size="14"></vs-icon>
                    </button>
                  </div>
                  <div *ngIf="selectedRowDimensions.length === 0" class="text-xs text-gray-400 italic p-1">
                    No row dimensions selected
                  </div>
                </div>
              </div>

              <!-- Col List -->
              <div>
                <div class="text-xs font-medium mb-1">Col List</div>
                <div class="space-y-0.5">
                  <div
                    *ngFor="let colDim of selectedColDimensions; let i = index"
                    class="flex items-center gap-1 p-1.5 bg-gray-50 rounded text-xs"
                  >
                    <span class="flex-1">{{ colDim.dimension.title }}</span>
                    <button
                      vs-button
                      appearance="clear"
                      size="sm"
                      class="!px-1 !min-w-[24px] !flex !items-center !justify-center"
                      [disabled]="i === 0"
                      (click)="moveColDimensionUp(i)"
                    >
                      <vs-icon name="chevron-up" size="16"></vs-icon>
                    </button>
                    <button
                      vs-button
                      appearance="clear"
                      size="sm"
                      class="!px-1 !min-w-[24px] !flex !items-center !justify-center"
                      [disabled]="i === selectedColDimensions.length - 1"
                      (click)="moveColDimensionDown(i)"
                    >
                      <vs-icon name="chevron-down" size="16"></vs-icon>
                    </button>
                    <button
                      vs-button
                      appearance="clear"
                      size="sm"
                      theme="danger"
                      class="!px-1 !min-w-[24px] !flex !items-center !justify-center"
                      (click)="removeColDimension(i)"
                    >
                      <vs-icon name="trash-03" size="14"></vs-icon>
                    </button>
                  </div>
                  <div *ngIf="selectedColDimensions.length === 0" class="text-xs text-gray-400 italic p-1">
                    No column dimensions selected
                  </div>
                </div>
              </div>
            </div>

            <!-- Filter List -->
            <div class="mt-2 border-t pt-2">
              <div class="text-xs font-medium mb-1">Filter List</div>
              <div class="space-y-2">
                <div
                  *ngFor="let filterDim of selectedFilterDimensions; let i = index"
                  class="border rounded p-2 bg-gray-50"
                >
                  <div class="flex items-center justify-between mb-1">
                    <span class="text-xs font-medium">{{ filterDim.dimension.title }}</span>
                    <button
                      vs-button
                      appearance="clear"
                      size="sm"
                      theme="danger"
                      class="!px-0.5"
                      (click)="removeFilterDimension(i)"
                      title="Remove filter"
                    >
                      <vs-icon name="trash-03" size="14"></vs-icon>
                    </button>
                  </div>
                  <vs-select
                    vsInput
                    class="!text-xs"
                    [value]="getSelectedFilterElements(i)"
                    [config]="{
                      multiple: true,
                      placeholder: 'Select elements...'
                    }"
                    [compareWith]="compareElementsById"
                    (valueChanges)="onFilterElementsChange(i, $event)"
                  >
                    <li *ngFor="let element of filterDim.elements" vs-select-option [value]="element" class="!text-xs">
                      {{ element.title }}
                    </li>
                  </vs-select>
                </div>
                <div *ngIf="selectedFilterDimensions.length === 0" class="text-xs text-gray-400 italic p-1">
                  No filter dimensions selected
                </div>
              </div>
            </div>
          </div>
        </vs-card>

        <!-- Right Panel: Asset Selection -->
        <vs-card
          class="md:col-span-2 xl:col-span-1 overflow-hidden flex flex-col min-h-[320px] md:min-h-[380px] xl:min-h-[420px] max-h-[360px] md:max-h-[420px]"
        >
          <vs-card-header
            [config]="{ title: 'Asset' }"
            class="[&_h2]:!text-sm [&_h2]:!text-gray-900 !p-2"
          ></vs-card-header>
          <div class="p-2 overflow-y-auto space-y-2 flex-1 min-h-0">
            <!-- Asset Selection Button -->
            <div class="space-y-2">
              <div class="text-xs text-gray-500 mb-2">Select an asset to calculate pricing based on its properties</div>

              <button vs-button theme="primary" class="!w-full !text-sm" (click)="openAssetPickerModal()">
                <vs-icon name="search-lg"></vs-icon>
                {{ hasAssetValues() ? 'Edit Asset Details' : 'Enter Asset Details' }}
              </button>

              <!-- Display entered values -->
              <div *ngIf="hasAssetValues()" class="border rounded p-2 bg-gray-50">
                <div class="text-xs font-medium mb-1">Asset Properties Entered</div>
                <div class="space-y-1 max-h-40 overflow-y-auto pr-1">
                  <div *ngFor="let entry of getAssetEntries()" class="text-xs text-gray-600">
                    <span class="font-medium">{{ getAssetEntryLabel(entry.key) }}:</span>
                    <span class="ml-1">{{ entry.value }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Term Selection (from term dimension elements) -->
            <div class="space-y-2 border-t pt-2 mt-2">
              <div class="text-xs text-gray-500 mb-1">Select Term Element</div>
              <vs-select
                vsInput
                class="!text-xs"
                [value]="selectedTermId"
                [config]="{
                  placeholder: 'Select term element...'
                }"
                (valueChanges)="onTermChange($event)"
              >
                <li *ngFor="let termElement of termElements" vs-select-option [value]="termElement.id" class="!text-xs">
                  {{ termElement.title }}
                </li>
              </vs-select>
              <div *ngIf="termElements.length === 0" class="text-xs text-gray-400 italic">
                No term elements found. Terms should be synced from policy to the term dimension.
              </div>
            </div>

            <!-- Add-on Selection (optional coverages for this term) -->
            <div *ngIf="selectedTermId" class="space-y-2 border-t pt-2 mt-2">
              <div class="text-xs text-gray-500 mb-1">Add-ons</div>

              <!-- Optional coverages as multi-select dropdown -->
              <vs-select
                *ngIf="availableCoverages.length > 0"
                vsInput
                class="!text-xs"
                [value]="selectedOptionalCoverageOptionIds"
                [config]="{
                  multiple: true,
                  placeholder: 'Select add-ons...'
                }"
                (valueChanges)="onCoveragesChange($event)"
              >
                <li *ngFor="let coverage of availableCoverages" vs-select-option [value]="coverage.id" class="!text-xs">
                  {{ coverage.title }}
                </li>
              </vs-select>

              <div *ngIf="availableCoverages.length === 0" class="text-xs text-gray-400 italic">
                No add-ons available for the selected term.
              </div>
            </div>

            <!-- Pricing Dimensions Selection -->
            <div *ngIf="pricingDimensions.length > 0" class="space-y-2 border-t pt-2 mt-2">
              <div class="text-xs text-gray-500 mb-1">Select Pricing Dimensions</div>
              <vs-select
                vsInput
                class="!text-xs"
                [value]="selectedPricingDimensionIds"
                [config]="{
                  multiple: true,
                  placeholder: 'Select pricing dimensions...'
                }"
                (valueChanges)="onPricingDimensionsChange($event)"
              >
                <li
                  *ngFor="let dimension of pricingDimensions"
                  vs-select-option
                  [value]="dimension.id"
                  class="!text-xs"
                >
                  {{ dimension.title }}
                </li>
              </vs-select>
              <div *ngIf="pricingDimensions.length === 0" class="text-xs text-gray-400 italic">
                No pricing dimensions available
              </div>
            </div>

            <!-- Distributor selection — scopes the preview to a seller -->
            <div class="space-y-2 border-t pt-2 mt-2">
              <div class="text-xs text-gray-500 mb-1">Select Distributor (optional)</div>
              <ws-distributor-picker
                [value]="selectedDistributor"
                [onlyActive]="true"
                [includeSubDistributors]="true"
                [showClearButton]="true"
                placeholder="All distributors (no seller)"
                (valueChanges)="onDistributorSelected($event)"
              ></ws-distributor-picker>
            </div>

            <!-- Dynamic Pricing Preview (shown for both modes) -->
            <div class="border-t pt-1.5 mt-2">
              <div class="mb-2">
                <div class="text-xs text-gray-500 mb-1">Pricing as of date (optional)</div>
                <div class="flex items-center gap-2 mb-2">
                  <button
                    vs-button
                    appearance="outline"
                    size="sm"
                    class="!text-xs !px-2 !py-1 !h-auto"
                    (click)="openAsOfDateModal(asOfDateDialog)"
                  >
                    {{ getAsOfDateButtonLabel() }}
                  </button>
                  <button
                    vs-button
                    appearance="outline"
                    size="sm"
                    class="!text-xs !px-2 !py-1 !h-auto"
                    [disabled]="!asOfDate"
                    (click)="clearAsOfDate()"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div class="flex items-center justify-between mb-1">
                <div class="text-xs text-gray-500">Dynamic Pricing Preview</div>
                <div class="flex items-center gap-2">
                  <button
                    *ngIf="hasAssetValues() && selectedTermId"
                    vs-button
                    appearance="outline"
                    size="sm"
                    class="!text-xs !px-2 !py-1 !h-auto"
                    [disabled]="loadingPricing"
                    (click)="calculatePricingPreview()"
                  >
                    {{ loadingPricing ? 'Calculating...' : 'Calculate' }}
                  </button>
                </div>
              </div>

              <div *ngIf="!loadingPricing && pricingPreview" class="space-y-1">
                <div
                  *ngIf="!pricingPreview.dynamicRatingUsed"
                  class="text-xs text-amber-600 bg-amber-50 p-1.5 rounded border border-amber-200"
                >
                  No pricing dimensions found. Dynamic rating not applied.
                </div>

                <!-- Total price displayed at the top -->
                <div class="border-b pb-1 mb-1">
                  <div class="flex justify-between items-center font-bold text-gray-900">
                    <span>Total Retail:</span>
                    <span class="text-base text-base-black"> {{ (getTotalPrice() / 100) | currency }} </span>
                  </div>
                  <!-- Why the total was floored — shown right under the number -->
                  <div *ngIf="pricingPreview.flooredNotes.length" class="mt-0.5">
                    <div
                      *ngFor="let note of pricingPreview.flooredNotes"
                      class="text-[10px] text-danger-600 font-medium"
                    >
                      {{ note }}
                    </div>
                  </div>
                  <!-- One-time fees: charged once with the first payment, never on renewal -->
                  <div
                    *ngIf="pricingPreview.oneTimeIntersections?.length"
                    class="flex justify-between items-center text-gray-700 mt-0.5"
                  >
                    <span>One-time fees (first payment only):</span>
                    <span>{{ (pricingPreview.oneTimePrice / 100) | currency }}</span>
                  </div>
                </div>

                <div class="space-y-1">
                  <div *ngIf="pricingPreview.oneTimeIntersections?.length" class="border-b pb-1">
                    <div class="font-medium mb-1">One-time fees</div>
                    <div class="pl-2 border-l-2 border-gray-200">
                      <div *ngFor="let intersection of pricingPreview.oneTimeIntersections" class="text-[10px] mb-0.5">
                        <span *ngFor="let element of intersection.elements; let isLast = last">
                          <span class="font-medium">{{ element.dimensionTitle }}</span>
                          <span class="text-gray-700"> ({{ element.elementTitle }})</span>
                          <span *ngIf="!isLast" class="text-gray-500 mx-1">+</span>
                        </span>
                        <span class="text-gray-500 mx-1">= {{ intersection.cellValue | currency }}</span>
                        <div class="pl-2 text-gray-500">
                          Fee Type: {{ intersection.feeType?.title ?? 'None' }} · Recipient: {{ intersection.recipient ?
                          describeRecipient(intersection.recipient) : 'Administrator (default)' }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="border-b pb-1 last:border-0">
                    <div *ngIf="!pricingPreview.cellFound" class="text-amber-600 text-[10px] mb-1">
                      No matching cell found
                    </div>

                    <!-- Show intersections: Fee Type (Miscellaneous) + Coverage / Add-ons + Term + other dimensions = value -->
                    <div *ngIf="pricingPreview.intersections.length > 0">
                      <div class="font-medium mb-1">Intersections</div>
                      <div class="pl-2 border-l-2 border-gray-200">
                        <!-- Show each complete intersection: element a + element b + element c = cell value -->
                        <div
                          *ngFor="let intersection of getUniqueIntersections(pricingPreview.intersections)"
                          class="text-[10px] mb-0.5"
                        >
                          <span *ngFor="let element of intersection.elements; let isLast = last">
                            <span class="font-medium">{{ element.dimensionTitle }}</span>
                            <span class="text-gray-700"> ({{ element.elementTitle }})</span>
                            <span *ngIf="!isLast" class="text-gray-500 mx-1">+</span>
                          </span>
                          <span *ngIf="intersection.formula as formula" class="text-gray-500 mx-1">
                            = {{ formula.percent }}% × {{ formula.baseAmount | currency }} ({{
                            formula.feeTypeTitles.join(', ') }})
                          </span>
                          <span
                            *ngIf="intersection.cellValue !== null && intersection.cellValue !== undefined"
                            class="text-gray-500 mx-1"
                          >
                            = {{ intersection.cellValue | currency }}
                          </span>
                          <div class="pl-2 text-gray-500">
                            Fee Type: {{ intersection.feeType?.title ?? 'None' }} · Recipient:
                            <span [class.text-warning-600]="!intersection.recipient"
                              >{{ describeRecipient(intersection.recipient) }}</span
                            >
                          </div>
                        </div>
                        <!-- Fallback for intersections without elements array (backward compatibility) -->
                        <ng-container *ngFor="let intersection of pricingPreview.intersections">
                          <div *ngIf="!intersection.elements || intersection.elements.length === 0" class="text-[10px]">
                            <span class="font-medium">{{ intersection.dimensionTitle }}</span>
                            <span class="text-gray-700"> ({{ intersection.elementTitle || 'No match' }})</span>
                            <span
                              *ngIf="intersection.cellValue !== null && intersection.cellValue !== undefined"
                              class="text-gray-500 mx-1"
                            >
                              = {{ intersection.cellValue | currency }}
                            </span>
                          </div>
                        </ng-container>
                      </div>
                    </div>

                    <!-- Other notes; floored-total ones render under Total Retail -->
                    <div
                      *ngIf="!pricingPreview.flooredToZero && pricingPreview.notes && pricingPreview.notes.length > 0"
                      class="mt-0.5"
                    >
                      <div *ngFor="let note of pricingPreview.notes" class="text-[10px] text-gray-500 italic">
                        {{ note }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div *ngIf="!loadingPricing && !pricingPreview" class="text-xs text-gray-400 italic">
                Enter asset details to calculate pricing
              </div>
            </div>
          </div>
        </vs-card>
      </div>

      <!-- Asset Input Modal -->
      <div *ngIf="showAssetPickerModal" class="fixed inset-0 z-[9999]" style="background-color: white">
        <div class="w-full h-full overflow-y-auto" style="background-color: white">
          <div class="max-w-6xl mx-auto px-6 py-8">
            <!-- Header -->
            <div class="relative flex flex-col items-center gap-4 mb-8">
              <div class="flex gap-2 w-full items-center">
                <div class="w-12 h-12"></div>
                <div class="flex justify-center items-center flex-grow">
                  <vs-featured-icon name="database-01" theme="primary"></vs-featured-icon>
                </div>
                <div class="w-12 h-12">
                  <button vs-icon-button appearance="clear" theme="default" size="lg" (click)="closeAssetPickerModal()">
                    <vs-icon name="x-close" size="20"></vs-icon>
                  </button>
                </div>
              </div>
              <div class="text-3xl font-semibold leading-[38px] text-center">
                Enter asset details for pricing calculation
              </div>
              <div class="text-sm text-gray-600">Please enter the asset details to calculate warranty pricing.</div>
            </div>

            <!-- Asset Input Form -->
            <div class="grid place-items-center mb-8">
              <vs-property-set-input
                *ngIf="propertySetId"
                [setId]="propertySetId"
                [formControl]="assetInfo"
                class="max-w-lg w-full"
              ></vs-property-set-input>
              <div *ngIf="!propertySetId" class="text-center py-8 text-gray-500">
                No property set configured for this policy.
              </div>
            </div>

            <!-- Footer Buttons -->
            <div class="flex gap-3">
              <button vs-button appearance="outline" (click)="closeAssetPickerModal()">Back</button>
              <button
                vs-button
                theme="primary"
                (click)="confirmAssetInput()"
                [disabled]="!assetInfo.valid || !assetInfo.value"
              >
                <div class="flex gap-3 items-center">
                  <span>Continue</span>
                  <vs-icon *ngIf="assetInfo.valid && assetInfo.value" name="check"></vs-icon>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <vs-dialog #asOfDateDialog [config]="{ dialogContainerClass: 'min-w-[460px] max-w-[600px]' }">
        <vs-dialog-header
          [config]="{
            mode: 'simple',
            hideCloseButton: false,
            showDivider: true,
            icon: 'calendar',
            iconTheme: 'primary',
            title: 'Pricing As Of Date',
            subtitle: 'Choose the date used for preview pricing.'
          }"
          (close)="closeAsOfDateModal(asOfDateDialog)"
        ></vs-dialog-header>

        <div class="p-4">
          <p class="text-sm font-medium text-gray-900 mt-2 mb-2">Select Pricing Date</p>
          <p class="text-sm font-normal text-gray-600 mb-4">
            If left unchanged, preview pricing uses today by default.
          </p>

          <div class="max-w-sm">
            <vs-date-picker
              *ngIf="renderAsOfDatePicker"
              [clearSelectionButton]="false"
              [value]="toDateValue(asOfDateModalValue)"
              (selectionChanged)="onAsOfDateModalSelectionChanged($any($event))"
            ></vs-date-picker>
          </div>

          <div class="flex justify-end items-center mt-4 gap-2">
            <button vs-button size="md" appearance="outline" (click)="closeAsOfDateModal(asOfDateDialog)">
              Cancel
            </button>
            <button vs-button size="md" theme="primary" (click)="saveAsOfDateModal(asOfDateDialog)">Apply</button>
          </div>
        </div>
      </vs-dialog>

      <vs-dialog #effectiveDateDialog [config]="{ dialogContainerClass: 'min-w-[535px] max-w-[800px]' }">
        <vs-dialog-header
          [config]="{
            mode: 'simple',
            hideCloseButton: false,
            showDivider: true,
            icon: 'calendar',
            iconTheme: 'primary',
            title: 'Edit Cell Settings',
            subtitle: 'Set date boundaries and the recipient for this matrix cell value.'
          }"
          (close)="closeEffectiveDateModal(effectiveDateDialog)"
        ></vs-dialog-header>

        <div class="p-4">
          <p class="text-sm font-medium text-gray-900 mt-2 mb-2">Configure Effective Date Window</p>
          <p class="text-sm font-normal text-gray-600 mb-2">Leave either side blank for an open boundary.</p>
          <p class="text-sm font-normal text-gray-600 mb-4">Boundaries are inclusive for pricing eligibility.</p>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div class="mb-1 text-xs text-gray-600">Effective From</div>
              <vs-date-picker
                *ngIf="renderEffectiveDatePickers"
                [clearSelectionButton]="true"
                [selectCurrentDayByDefault]="false"
                [value]="toDateValue(effectiveDateModalFrom)"
                (selectionChanged)="onEffectiveDateModalFromSelectionChanged($any($event))"
              ></vs-date-picker>
            </div>

            <div>
              <div class="mb-1 text-xs text-gray-600">Effective To</div>
              <vs-date-picker
                *ngIf="renderEffectiveDatePickers"
                [clearSelectionButton]="true"
                [selectCurrentDayByDefault]="false"
                [value]="toDateValue(effectiveDateModalTo)"
                (selectionChanged)="onEffectiveDateModalToSelectionChanged($any($event))"
              ></vs-date-picker>
            </div>
          </div>

          <p *ngIf="!isEffectiveDateModalValid()" class="text-danger-500 text-sm mt-3">
            Effective From must be on or before Effective To.
          </p>

          <div class="border-t mt-4 pt-4">
            <p class="text-sm font-medium text-gray-900 mb-1">Recipient Override</p>
            <p class="text-sm font-normal text-gray-600 mb-3">
              Who this cell's amount is intended for, instead of its Fee Type's default recipient. Does not affect
              pricing. Manage parties on the Parties tab.
            </p>
            <ws-fee-recipient-picker
              *ngIf="effectiveDateModalView?.cell?.id; else saveCellFirst"
              unassignedLabel="Use Fee Type default"
              [value]="effectiveDateModalRecipient"
              (valueChanges)="effectiveDateModalRecipient = $event"
            ></ws-fee-recipient-picker>
            <ng-template #saveCellFirst>
              <p class="text-xs text-gray-500 italic">Save a value for this cell before assigning a recipient.</p>
            </ng-template>
          </div>

          <div class="flex justify-between items-center mt-4">
            <div class="flex gap-2 ml-auto">
              <button vs-button size="md" appearance="outline" (click)="closeEffectiveDateModal(effectiveDateDialog)">
                Cancel
              </button>
              <button
                vs-button
                size="md"
                theme="primary"
                [disabled]="!isEffectiveDateModalValid()"
                (click)="saveEffectiveDateModal(effectiveDateDialog)"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </vs-dialog>

      <!-- Fee recipient review: cells that would issue on the legacy fallback attribution -->
      <div
        *ngIf="recipientReviewCells.length"
        class="w-full mb-2 px-3 py-2 border border-warning-200 bg-warning-50 rounded-lg text-xs text-warning-800"
      >
        <div class="flex items-center gap-2">
          <vs-icon name="alert-triangle" size="14"></vs-icon>
          <span class="flex-1">
            {{ recipientReviewCells.length }} cell(s) have no fee recipient and will be attributed to the insurer by
            default.
          </span>
          <button
            vs-button
            appearance="clear"
            size="sm"
            type="button"
            (click)="showRecipientReview = !showRecipientReview"
          >
            {{ showRecipientReview ? 'Hide' : 'Review' }}
          </button>
        </div>
        <ul *ngIf="showRecipientReview" class="mt-2 pl-6 list-disc space-y-0.5">
          <li *ngFor="let item of recipientReviewCells">{{ describeRecipientReview(item) }}</li>
        </ul>
      </div>

      <!-- Bottom: Matrix Grid - Full Width -->
      <vs-card class="w-full overflow-hidden" style="min-height: calc(100vh - 500px)">
        <vs-card-header
          [config]="{ title: 'Matrix' }"
          class="[&_h2]:!text-sm [&_h2]:!text-gray-900 !p-2"
        ></vs-card-header>
        <div class="overflow-auto" style="max-height: calc(100vh - 500px)">
          <div *ngIf="loading" class="p-2 text-xs text-gray-500">Loading...</div>
          <div
            *ngIf="!loading && matrixRows.length === 0 && matrixColumns.length === 0"
            class="p-2 text-xs text-gray-400"
          >
            Select row or column dimensions to display the matrix
          </div>
          <table
            *ngIf="!loading && (matrixRows.length > 0 || matrixColumns.length > 0)"
            class="min-w-full text-xs border-collapse"
          >
            <thead class="bg-base-light sticky top-0 z-20">
              <tr>
                <th
                  class="px-2 py-1 text-left font-semibold border sticky left-0 bg-slate-50 z-30 whitespace-nowrap shadow-[2px_0_4px_rgba(0,0,0,0.1)]"
                >
                  Row / Col
                </th>
                <th
                  *ngFor="let col of matrixColumns; trackBy: trackMatrixColumn"
                  class="px-2 py-1 text-left font-semibold border min-w-[100px] whitespace-nowrap bg-slate-50"
                  [title]="col.colLabels.join(' / ')"
                >
                  {{ col.colLabels.join(' ') }}
                </th>
                <th
                  *ngIf="matrixColumns.length === 0"
                  class="px-2 py-1 text-left font-semibold border min-w-[100px] whitespace-nowrap bg-slate-50"
                >
                  Value
                </th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of matrixRows; trackBy: trackMatrixRow" class="border-t hover:bg-gray-50">
                <th
                  class="px-2 py-1 text-left font-medium bg-slate-50 border sticky left-0 z-10 whitespace-nowrap shadow-[2px_0_4px_rgba(0,0,0,0.1)]"
                  [title]="row.rowLabels.join(' / ')"
                >
                  {{ row.rowLabels.join(' ') }}
                </th>
                <td
                  *ngFor="let col of matrixColumns; trackBy: trackMatrixColumn"
                  class="px-2 py-1 align-middle border min-w-[100px] bg-white"
                >
                  <ng-container *ngIf="getCellView(row.rowKey, col.colKey) as view">
                    <ng-container *ngIf="canUseBooleanValues(); else numericCellEditor">
                      <div class="flex items-center gap-1">
                        <label class="inline-flex items-center justify-center flex-1 cursor-pointer select-none">
                          <input
                            vsInput
                            type="checkbox"
                            appearance="toggle"
                            class="block"
                            [ngModel]="!!view.value"
                            (ngModelChange)="onBooleanCellToggle(view, $event)"
                            [disabled]="saving"
                          />
                        </label>
                        <button
                          vs-button
                          appearance="outline"
                          size="sm"
                          class="!text-[10px] !px-2 !py-1 !h-auto"
                          [disabled]="saving"
                          title="Edit cell settings"
                          (click)="openEffectiveDateModal(view, effectiveDateDialog)"
                        >
                          Configure
                        </button>
                      </div>
                    </ng-container>
                    <ng-template #numericCellEditor>
                      <div class="space-y-1">
                        <div class="flex items-center gap-1">
                          <input
                            *ngIf="view.valueType !== 'FORMULA'; else formulaCellLabel"
                            class="vs-input flex-1 min-w-0 text-xs py-1 px-2 border border-gray-300"
                            type="number"
                            step="0.01"
                            [ngModel]="view.value"
                            (ngModelChange)="onValueChange(view, $event)"
                            [disabled]="saving"
                            placeholder="—"
                          />
                          <ng-template #formulaCellLabel>
                            <button
                              type="button"
                              class="vs-input flex-1 min-w-0 text-left text-xs py-1 px-2 border border-gray-300 bg-gray-50"
                              [vsTooltip]="{ template: formulaTooltip, context: { formula: view.formula } }"
                              tooltipStyle="light"
                              [disabled]="saving"
                              (click)="openFormulaDialogForView(view, formulaDialog)"
                            >
                              {{ view.formula?.percent }}%
                            </button>
                          </ng-template>
                          <button
                            *ngIf="view.canUseFormula"
                            vs-icon-button
                            size="sm"
                            appearance="clear"
                            title="Fixed amount or percentage"
                            aria-label="Fixed amount or percentage"
                            [disabled]="saving"
                            (click)="openFormulaDialogForView(view, formulaDialog)"
                          >
                            <vs-icon name="percent-03"></vs-icon>
                          </button>
                          <button
                            vs-button
                            appearance="outline"
                            size="sm"
                            class="!text-[10px] !px-2 !py-1 !h-auto"
                            [disabled]="saving"
                            title="Edit cell settings"
                            (click)="openEffectiveDateModal(view, effectiveDateDialog)"
                          >
                            Configure
                          </button>
                        </div>
                      </div>
                    </ng-template>
                  </ng-container>
                </td>
                <td *ngIf="matrixColumns.length === 0" class="px-2 py-1 align-middle border min-w-[100px] bg-white">
                  <ng-container *ngIf="getCellView(row.rowKey, emptyAxisKey) as view">
                    <ng-container *ngIf="canUseBooleanValues(); else rowOnlyNumericCellEditor">
                      <div class="flex items-center gap-1">
                        <label class="inline-flex items-center justify-center flex-1 cursor-pointer select-none">
                          <input
                            vsInput
                            type="checkbox"
                            appearance="toggle"
                            class="block"
                            [ngModel]="!!view.value"
                            (ngModelChange)="onBooleanCellToggle(view, $event)"
                            [disabled]="saving"
                          />
                        </label>
                        <button
                          vs-button
                          appearance="outline"
                          size="sm"
                          class="!text-[10px] !px-2 !py-1 !h-auto"
                          [disabled]="saving"
                          title="Edit cell settings"
                          (click)="openEffectiveDateModal(view, effectiveDateDialog)"
                        >
                          Configure
                        </button>
                      </div>
                    </ng-container>
                    <ng-template #rowOnlyNumericCellEditor>
                      <div class="space-y-1">
                        <div class="flex items-center gap-1">
                          <input
                            *ngIf="view.valueType !== 'FORMULA'; else formulaCellLabel"
                            class="vs-input flex-1 min-w-0 text-xs py-1 px-2 border border-gray-300"
                            type="number"
                            step="0.01"
                            [ngModel]="view.value"
                            (ngModelChange)="onValueChange(view, $event)"
                            [disabled]="saving"
                            placeholder="—"
                          />
                          <ng-template #formulaCellLabel>
                            <button
                              type="button"
                              class="vs-input flex-1 min-w-0 text-left text-xs py-1 px-2 border border-gray-300 bg-gray-50"
                              [vsTooltip]="{ template: formulaTooltip, context: { formula: view.formula } }"
                              tooltipStyle="light"
                              [disabled]="saving"
                              (click)="openFormulaDialogForView(view, formulaDialog)"
                            >
                              {{ view.formula?.percent }}%
                            </button>
                          </ng-template>
                          <button
                            *ngIf="view.canUseFormula"
                            vs-icon-button
                            size="sm"
                            appearance="clear"
                            title="Fixed amount or percentage"
                            aria-label="Fixed amount or percentage"
                            [disabled]="saving"
                            (click)="openFormulaDialogForView(view, formulaDialog)"
                          >
                            <vs-icon name="percent-03"></vs-icon>
                          </button>
                          <button
                            vs-button
                            appearance="outline"
                            size="sm"
                            class="!text-[10px] !px-2 !py-1 !h-auto"
                            [disabled]="saving"
                            title="Edit cell settings"
                            (click)="openEffectiveDateModal(view, effectiveDateDialog)"
                          >
                            Configure
                          </button>
                        </div>
                      </div>
                    </ng-template>
                  </ng-container>
                </td>
              </tr>
              <tr *ngIf="matrixRows.length === 0" class="border-t hover:bg-gray-50">
                <th
                  class="px-2 py-1 text-left font-medium bg-slate-50 border sticky left-0 z-10 whitespace-nowrap shadow-[2px_0_4px_rgba(0,0,0,0.1)]"
                >
                  Value
                </th>
                <td
                  *ngFor="let col of matrixColumns; trackBy: trackMatrixColumn"
                  class="px-2 py-1 align-middle border min-w-[100px] bg-white"
                >
                  <ng-container *ngIf="getCellView(emptyAxisKey, col.colKey) as view">
                    <ng-container *ngIf="canUseBooleanValues(); else colOnlyNumericCellEditor">
                      <div class="flex items-center gap-1">
                        <label class="inline-flex items-center justify-center flex-1 cursor-pointer select-none">
                          <input
                            vsInput
                            type="checkbox"
                            appearance="toggle"
                            class="block"
                            [ngModel]="!!view.value"
                            (ngModelChange)="onBooleanCellToggle(view, $event)"
                            [disabled]="saving"
                          />
                        </label>
                        <button
                          vs-button
                          appearance="outline"
                          size="sm"
                          class="!text-[10px] !px-2 !py-1 !h-auto"
                          [disabled]="saving"
                          title="Edit cell settings"
                          (click)="openEffectiveDateModal(view, effectiveDateDialog)"
                        >
                          Configure
                        </button>
                      </div>
                    </ng-container>
                    <ng-template #colOnlyNumericCellEditor>
                      <div class="space-y-1">
                        <div class="flex items-center gap-1">
                          <input
                            *ngIf="view.valueType !== 'FORMULA'; else formulaCellLabel"
                            class="vs-input flex-1 min-w-0 text-xs py-1 px-2 border border-gray-300"
                            type="number"
                            step="0.01"
                            [ngModel]="view.value"
                            (ngModelChange)="onValueChange(view, $event)"
                            [disabled]="saving"
                            placeholder="—"
                          />
                          <ng-template #formulaCellLabel>
                            <button
                              type="button"
                              class="vs-input flex-1 min-w-0 text-left text-xs py-1 px-2 border border-gray-300 bg-gray-50"
                              [vsTooltip]="{ template: formulaTooltip, context: { formula: view.formula } }"
                              tooltipStyle="light"
                              [disabled]="saving"
                              (click)="openFormulaDialogForView(view, formulaDialog)"
                            >
                              {{ view.formula?.percent }}%
                            </button>
                          </ng-template>
                          <button
                            *ngIf="view.canUseFormula"
                            vs-icon-button
                            size="sm"
                            appearance="clear"
                            title="Fixed amount or percentage"
                            aria-label="Fixed amount or percentage"
                            [disabled]="saving"
                            (click)="openFormulaDialogForView(view, formulaDialog)"
                          >
                            <vs-icon name="percent-03"></vs-icon>
                          </button>
                          <button
                            vs-button
                            appearance="outline"
                            size="sm"
                            class="!text-[10px] !px-2 !py-1 !h-auto"
                            [disabled]="saving"
                            title="Edit cell settings"
                            (click)="openEffectiveDateModal(view, effectiveDateDialog)"
                          >
                            Configure
                          </button>
                        </div>
                      </div>
                    </ng-template>
                  </ng-container>
                </td>
                <td
                  *ngIf="matrixColumns.length === 0"
                  class="px-2 py-1 align-middle border min-w-[100px] bg-white text-gray-400"
                >
                  —
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </vs-card>
    </div>
  </div>
</vs-card>
